// apps/roleforge-app/src/components/materialize/MaterializeProgressDialog.tsx
// Plan 3c Task 3.8 — Modal that fires the materialize POST on mount and
// shows a 4-step progress indicator. Honest framing: the server runs
// materialize as one transaction, so this dialog animates the steps as the
// response arrives rather than streaming real per-step progress.
//
// Task 5 (#1738): this dialog no longer fetches the proposal itself. The
// caller (ProposalReviewPage, or MaterializePage on the one-click path) reads
// the proposal, and the version the reviewer actually SAW arrives here as a
// prop — the dialog just posts once with it.

import { useEffect, useRef, useState } from 'react';
import {
  fetchMaterializeStatus,
  postMaterialize,
  MaterializeApiError,
  type MaterializeResponse,
  type MaterializeStatusResponse,
} from '../../lib/materialize-api';

interface MaterializeProgressDialogProps {
  auditId: string;
  jwt: string;
  /**
   * REQ-ONE-11 — the Blueprint version the caller reviewed. `null` confirms
   * unversioned ("apply whatever is current"), which is only correct where
   * the confirmation IS the first look at the output.
   */
  blueprintVersion?: string | null;
  /** Task 5 (#1738): reviewer opted in to sending pre-staged invitations. */
  sendInvites?: boolean;
  dismissible?: boolean;
  variant?: 'overlay' | 'inline';
  onCancel?: () => void;
  /**
   * Called instead of rendering an error when the POST is refused with a 409
   * `blueprint_stale` — the review page re-fetches and re-renders with the
   * server's message. Never auto-retried here.
   */
  onBlueprintStale?: (message: string) => void;
  /** Optional override for redirect; defaults to window.location.href = response.redirect_url. */
  onRedirect?: (url: string) => void;
}

// REQ-ONE-11: step 1 used to say "Creating your agents and skills". The
// Blueprint no longer creates a team of agents — it sets up the company's
// departments and gives your AI the roles and skills to work in them.
const STEPS = [
  'Setting up your departments, roles, and skills',
  'Importing your AI Workforce Blueprint',
  'Preparing your workspace and site setup',
  'Sending you to VibeControl',
];

const FALLBACK_REDIRECT = 'https://vibecontrol.app';

/**
 * Issue #1323 (3) — this component set `errorMessage` straight from
 * `err.message` and rendered it at the buyer. That is `status=failed`,
 * `workspace org mismatch`, or a raw Postgres constraint string, shown to a
 * non-technical owner-operator who has just paid. The calm copy the server
 * already writes (`MATERIALIZE_FAILED_COPY`) was status-endpoint-only.
 *
 * Every failure now renders customer-safe copy; the raw string goes to the
 * console for support, and is kept in a `data-` attribute rather than in the
 * visible text.
 */
export const MATERIALIZE_GENERIC_FAILURE_COPY =
  'We could not finish setting up your workspace. Nothing you paid for is lost — your Blueprint is saved. Try again, and if it keeps happening, contact support and we will finish it for you.';

export function customerSafeMaterializeError(raw: unknown): string {
  const message = raw instanceof Error ? raw.message : String(raw ?? '');
  const lowered = message.toLowerCase();

  // REQ-ONE-11: the server's stale-Blueprint copy is already written FOR the
  // customer ("nothing was applied… confirm again"), and replacing it with the
  // generic failure line would hide the one thing they need to know — that
  // this is recoverable by looking again, not a broken setup.
  if (raw instanceof MaterializeApiError && raw.code === 'blueprint_stale') {
    return message;
  }
  if (lowered.includes('cap') && lowered.includes('credit')) {
    return 'This account has reached its credit limit, so setup could not run. Contact support and we will lift it.';
  }
  if (lowered.includes('failed to fetch') || lowered.includes('networkerror') || lowered.includes('network')) {
    return 'We lost the connection while setting up your workspace. Nothing is lost — check your connection and try again.';
  }
  if (lowered.includes('not_ready') || lowered.includes('not ready')) {
    return 'Your Blueprint still has a step to finish before we can set up your workspace. Go back to your Blueprint and this screen will let you through.';
  }
  if (lowered.includes('not_found') || lowered.includes('not found')) {
    return 'We could not find this Blueprint. If you reached this page from an email link, open it again from your account.';
  }
  return MATERIALIZE_GENERIC_FAILURE_COPY;
}

type Phase = 'running' | 'success' | 'cap-error' | 'error';

export function MaterializeProgressDialog({
  auditId,
  jwt,
  blueprintVersion = null,
  sendInvites = false,
  dismissible = true,
  variant = 'overlay',
  onCancel,
  onBlueprintStale,
  onRedirect,
}: MaterializeProgressDialogProps) {
  const [phase, setPhase] = useState<Phase>('running');
  const [completedSteps, setCompletedSteps] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** Raw server/DB string, kept for support — never rendered as copy. */
  const [rawError, setRawError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const animTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    let cancelled = false;
    setPhase('running');
    setCompletedSteps(0);
    setErrorMessage(null);
    setRawError(null);

    // Animate steps progressing every ~700ms while we wait.
    animTimers.current.forEach(clearTimeout);
    animTimers.current = [];
    [1, 2, 3].forEach((n) => {
      animTimers.current.push(
        setTimeout(() => {
          if (!cancelled) setCompletedSteps((c) => Math.max(c, n));
        }, n * 700),
      );
    });

    const redirectFromResponse = (res: MaterializeResponse | MaterializeStatusResponse) => {
      if (cancelled) return;
      if (res.status === 'materializing') {
        const retryMs = Math.max(res.retry_after_ms ?? 1500, 250);
        animTimers.current.push(setTimeout(() => void pollStatus(), retryMs));
        return;
      }
      if (res.status === 'failed') {
        animTimers.current.forEach(clearTimeout);
        animTimers.current = [];
        setPhase('error');
        // The status endpoint already writes MATERIALIZE_FAILED_COPY.
        setErrorMessage(res.message);
        setRawError(res.failure_code ?? null);
        return;
      }
      if (res.status === 'ready_to_materialize') {
        animTimers.current.push(setTimeout(() => void pollStatus(), 500));
        return;
      }
      animTimers.current.forEach(clearTimeout);
      animTimers.current = [];
      setCompletedSteps(STEPS.length);
      setPhase('success');
      setTimeout(() => {
        if (cancelled) return;
        const url = res.redirect_url ?? FALLBACK_REDIRECT;
        if (onRedirect) {
          onRedirect(url);
        } else if (typeof window !== 'undefined') {
          window.location.href = url;
        }
      }, 800);
    };

    const pollStatus = async () => {
      try {
        const status = await fetchMaterializeStatus(auditId, jwt);
        redirectFromResponse(status);
      } catch (err) {
        if (cancelled) return;
        animTimers.current.forEach(clearTimeout);
        animTimers.current = [];
        setPhase('error');
        setErrorMessage(customerSafeMaterializeError(err));
        setRawError(err instanceof Error ? err.message : String(err));
        console.error('[roleforge] materialize status poll failed', err);
      }
    };

    // REQ-ONE-11 / Task 5 (#1738): the confirmation is bound to the Blueprint
    // version the CALLER reviewed and passed down — this dialog no longer
    // re-fetches the proposal, so what was on the review screen is exactly
    // what is confirmed. If the Blueprint moved in between, the server
    // refuses with 409 `blueprint_stale` and the review page re-renders.
    postMaterialize(auditId, jwt, blueprintVersion, { sendInvites })
      .then(redirectFromResponse)
      .catch((err: unknown) => {
        if (cancelled) return;
        animTimers.current.forEach(clearTimeout);
        animTimers.current = [];
        if (
          onBlueprintStale
          && err instanceof MaterializeApiError
          && err.code === 'blueprint_stale'
        ) {
          // The review page owns this case: it re-fetches the proposal and
          // shows the server's message. Never auto-retry a stale confirm.
          onBlueprintStale(err.message);
          return;
        }
        if (err instanceof MaterializeApiError && err.status === 402) {
          setPhase('cap-error');
          setErrorMessage(err.message ?? 'Audit credit cap reached.');
        } else {
          setPhase('error');
          setErrorMessage(customerSafeMaterializeError(err));
          setRawError(err instanceof Error ? err.message : String(err));
          console.error('[roleforge] materialize failed', err);
        }
      });

    return () => {
      cancelled = true;
      animTimers.current.forEach(clearTimeout);
      animTimers.current = [];
    };
    // attempt is the retry trigger; auditId/jwt/blueprintVersion rarely change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId, jwt, blueprintVersion, sendInvites, attempt]);

  const isInline = variant === 'inline';

  // A11y: as a modal overlay this must announce itself as such and pull
  // keyboard focus inside, so tabbing cannot land on the page behind it.
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isInline) panelRef.current?.focus();
  }, [isInline]);

  return (
    <div
      role={isInline ? 'status' : 'dialog'}
      aria-modal={isInline ? undefined : true}
      aria-label="Setting up your workforce in VibeControl"
      data-testid="materialize-progress-dialog"
      className={
        isInline
          ? 'w-full'
          : 'fixed inset-0 z-40 flex items-center justify-center bg-ink/40 p-4'
      }
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={
          isInline
            ? 'w-full rounded-lg border border-line bg-white/5 p-5 shadow-soft'
            : 'bg-paper rounded-2xl shadow-soft w-full max-w-md p-6 outline-none'
        }
      >
        <h2 className="text-xl font-semibold text-ink mb-4">
          Setting up your workforce in VibeControl
        </h2>

        <ol className="space-y-3 mb-6" data-testid="materialize-step-list">
          {STEPS.map((label, idx) => {
            const done = idx < completedSteps;
            const active = idx === completedSteps && phase === 'running';
            return (
              <li
                key={label}
                className="flex items-center gap-3 text-sm"
                data-testid={`materialize-step-${idx}`}
                data-state={done ? 'done' : active ? 'active' : 'pending'}
              >
                <span
                  aria-hidden="true"
                  className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs ${
                    done
                      ? 'bg-mint text-paper'
                      : active
                        ? 'bg-plum/20 text-plum animate-pulse'
                        : 'bg-line text-graphite'
                  }`}
                >
                  {done ? '✓' : active ? '…' : '•'}
                </span>
                <span className={done ? 'text-ink' : 'text-graphite'}>{label}</span>
              </li>
            );
          })}
        </ol>

        {phase === 'cap-error' && (
          <div className="rounded-lg bg-ember/10 text-ember p-3 mb-4 text-sm" data-testid="materialize-cap-error">
            <p className="font-medium mb-1">You have reached your plan's limit.</p>
            <p className="mb-2">{errorMessage}</p>
            <p>
              <a
                href="mailto:support@vibecontrol.app"
                className="underline hover:no-underline"
                data-testid="materialize-cap-support-link"
              >
                Contact support
              </a>{' '}
              to activate your account.
            </p>
          </div>
        )}

        {phase === 'error' && (
          <div
            className="rounded-lg bg-ember/10 text-ember p-3 mb-4 text-sm"
            data-testid="materialize-error"
            data-raw-error={rawError ?? undefined}
          >
            <p className="font-medium mb-1">Setup did not finish.</p>
            <p>{errorMessage ?? MATERIALIZE_GENERIC_FAILURE_COPY}</p>
            <p className="mt-2">
              <a
                href="mailto:support@vibecontrol.app"
                className="underline hover:no-underline"
                data-testid="materialize-error-support-link"
              >
                Contact support
              </a>{' '}
              and we will finish it for you.
            </p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          {phase === 'error' && (
            <button
              type="button"
              onClick={() => setAttempt((a) => a + 1)}
              className="px-4 py-2 rounded bg-plum text-paper font-semibold hover:bg-plum/90"
              data-testid="materialize-retry"
            >
              Try again
            </button>
          )}
          {(phase !== 'success' && dismissible !== false && onCancel) && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded border border-line text-graphite hover:bg-line/30"
              data-testid="materialize-close"
            >
              {phase === 'running' ? 'Hide' : 'Close'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
